import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Resolver } from 'react-hook-form';
import { schema } from './schema/schema';
import {
  OverlayStyled,
  PopupStyled,
  RequestContainer,
  StyledLayer,
} from './styles/NewRequestPopUp.styled';
import { NewRequestForm } from './newRequestForm/NewRequestForm';
import { useCreateRequest } from './hooks/useCreateRequest';
import { VacationType } from 'src/tabs/hooks/useVacationTypes';
import { appearance } from 'src/hooks/useAlertMessage';
import { getDatesInRange } from 'src/utils/dateFns/getDateRange';
import { EmployeeVacationDays } from '../../../tabs/models/SummaryInterfaces';
import {
  REQUESTED_VACATION_EXCEEDING_AVAILABLE_VACATION_DAYS_ERROR_MESSAGE,
  TIMEOFF_SUBMIT_SUCCESS_MESSAGE,
  USE_BONUS_DAYS_BEFORE_BASE_PAID_DAYS_ERROR_MESSAGE,
  VacationTypes,
} from 'src/tabs/constants/Timeoff.constants';

export interface IForm {
  startDate: Date | null;
  endDate: Date | null;
  vacationType: NonNullable<
    | 'Court Duty Time-off'
    | 'Sick Leave'
    | 'Blood Donation Vacation'
    | 'Deceased Relative Time-off'
    | 'Marriage Vacation'
    | 'Maternity Time-off'
    | 'Paid Education Vacation'
    | VacationTypes.PAID_VACATION
    | VacationTypes.PAID_VACATION_BONUS_DAYS
    | 'Paternity Time-off'
    | VacationTypes.UNPAID_VACATION
    | ''
  >;
  employeeComment: string;
}

interface NewRequestPopUpProps {
  isPopupVisible: boolean;
  hidePopup: () => void;
  setShowMessage: (message: string, appearance: appearance) => void;
  holidays: Array<string>;
  vacationTypes: VacationType[];
  getUserRequests: (
    statusCode?: string[],
    vacationTypeId?: string[],
    vacationTypes?: VacationType[]
  ) => Promise<void>;
  vacationType: string[];
  vacationStatus: string[];
  getSummary: (bonusDays?: number, paidDays?: number) => Promise<void>;
  vacationPeriodNextYear: EmployeeVacationDays;
  selectedYear: number;
  vacationPeriod: EmployeeVacationDays;
  mappedVacationTypes: {
    key: string;
    text: string;
  }[];
}

export const NewRequestPopUp = ({
  getSummary,
  hidePopup,
  isPopupVisible,
  setShowMessage,
  holidays,
  vacationTypes,
  getUserRequests,
  vacationStatus,
  vacationType,
  vacationPeriodNextYear,
  selectedYear,
  vacationPeriod,
  mappedVacationTypes,
}: NewRequestPopUpProps) => {
  const { createRequest } = useCreateRequest(vacationTypes);
  const { handleSubmit, control, reset, setError } = useForm<IForm>({
    resolver: yupResolver(schema) as Resolver<IForm>,
    mode: 'onSubmit',
    defaultValues: {
      startDate: null,
      endDate: null,
      vacationType: '',
      employeeComment: '',
    },
  });

  const submitHandler = async (data: IForm) => {
    if (
      data.vacationType === VacationTypes.PAID_VACATION_BONUS_DAYS &&
      vacationPeriod.new_remainingbasepaiddays > 0
    ) {
      setError('vacationType', {
        type: 'custom',
        message: USE_BONUS_DAYS_BEFORE_BASE_PAID_DAYS_ERROR_MESSAGE,
      });
      return;
    }
    if (isRequestedVacationExceedingAvailableVacationDays(data)) {
      setError('endDate', {
        type: 'custom',
        message:
          REQUESTED_VACATION_EXCEEDING_AVAILABLE_VACATION_DAYS_ERROR_MESSAGE,
      });
      return;
    }
    await createRequest(data);
    reset();
    await getUserRequests(vacationStatus, vacationType, vacationTypes);
    setShowMessage(TIMEOFF_SUBMIT_SUCCESS_MESSAGE, 'success');
    if (
      data.vacationType === VacationTypes.PAID_VACATION_BONUS_DAYS &&
      vacationPeriod.new_remainingbonuspaiddays > 0
    ) {
      await getSummary(
        getDatesInRange(data.startDate, data.endDate, holidays).length,
        0
      );
    } else if (
      data.vacationType === VacationTypes.PAID_VACATION &&
      vacationPeriod.new_remainingbasepaiddays > 0
    ) {
      await getSummary(
        0,
        getDatesInRange(data.startDate, data.endDate, holidays).length
      );
    } else {
      await getSummary();
    }
    hidePopup();
  };

  const isRequestedVacationExceedingAvailableVacationDays = (data: IForm) => {
    let vacationDays = 0;
    for (
      let date = new Date(data.startDate);
      date <= new Date(data.endDate);
      date.setDate(date.getDate() + 1)
    ) {
      if (
        date.getDay() % 6 === 0 ||
        holidays.some(
          (holiday) =>
            new Date(holiday).setHours(0, 0, 0, 0) === date.setHours(0, 0, 0, 0)
        )
      ) {
        continue;
      }
      vacationDays++;
    }

    return (
      (data.vacationType === VacationTypes.PAID_VACATION &&
        vacationPeriod.new_remainingbasepaiddays < vacationDays) ||
      (data.vacationType === VacationTypes.PAID_VACATION_BONUS_DAYS &&
        vacationPeriod.new_remainingbonuspaiddays < vacationDays)
    );
  };

  const handleClosePopup = () => {
    reset();
    hidePopup();
  };

  return (
    <>
      {isPopupVisible && (
        <StyledLayer>
          <PopupStyled role="dialog" aria-modal="true" onDismiss={hidePopup}>
            <OverlayStyled onClick={handleClosePopup} />
            <RequestContainer role="document">
              <NewRequestForm
                mappedVacationTypes={mappedVacationTypes}
                selectedYear={selectedYear}
                vacationPeriodNextYear={vacationPeriodNextYear}
                holidays={holidays}
                control={control}
                handleSubmit={handleSubmit}
                submitHandler={submitHandler}
                hidePopup={hidePopup}
                reset={reset}
              />
            </RequestContainer>
          </PopupStyled>
        </StyledLayer>
      )}
    </>
  );
};
